import React, { useState } from 'react';
import axios from 'axios';
import { Store, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function UpgradeToCreatorModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    store_name: '',
    store_slug: '',
    bio: ''
  });

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleStoreNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      store_name: name,
      store_slug: generateSlug(name)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.store_name.trim()) {
      toast.error('Please enter a store name');
      return;
    }
    
    if (!formData.store_slug.trim()) {
      toast.error('Please enter a store URL');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/creators`, {
        store_name: formData.store_name,
        store_slug: formData.store_slug,
        bio: formData.bio || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local storage with new role
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.role = 'creator';
      localStorage.setItem('user', JSON.stringify(storedUser));

      toast.success('Congratulations! Your creator account is ready!');
      onSuccess(response.data);
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create store';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00F0FF] to-[#00FF94] rounded-xl flex items-center justify-center">
              <Store className="w-5 h-5 text-white" />
            </div>
            Become a Creator
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <p className="text-neutral-400 text-sm">
            Upgrade your account to start selling AI products. You'll keep all your existing purchases and affiliate links!
          </p>

          <div>
            <label className="block text-sm font-semibold mb-2">Store Name *</label>
            <Input
              value={formData.store_name}
              onChange={handleStoreNameChange}
              className="bg-neutral-800 border-white/10"
              placeholder="e.g., AI Tools Pro"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Store URL *</label>
            <div className="flex items-center gap-2">
              <span className="text-neutral-500 text-sm">theailedger.ai/store/</span>
              <Input
                value={formData.store_slug}
                onChange={(e) => setFormData({ ...formData, store_slug: generateSlug(e.target.value) })}
                className="bg-neutral-800 border-white/10 flex-1"
                placeholder="my-store"
                required
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1">
              This will be your unique store link
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Bio (optional)</label>
            <Input
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              className="bg-neutral-800 border-white/10"
              placeholder="Tell customers about yourself..."
            />
          </div>

          <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl p-4">
            <h4 className="font-semibold text-[#00F0FF] mb-2">What you get as a Creator:</h4>
            <ul className="text-sm text-neutral-300 space-y-1">
              <li>• Your own storefront with shareable link</li>
              <li>• Sell unlimited AI products</li>
              <li>• Instant payouts via Stripe</li>
              <li>• Affiliate program for your products</li>
              <li>• Analytics dashboard</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-[#00F0FF] to-[#00FF94] text-black font-bold rounded-xl hover:opacity-90"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Store...
                </>
              ) : (
                'Create My Store'
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-white/10 hover:bg-white/5 rounded-xl"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeToCreatorModal;
